import { Injectable, signal, computed } from '@angular/core';

/** Types of secondary panel content */
export type SecondaryPanelType = 'quickAction' | null;

/** Quick action identifiers */
export type QuickActionId = 'createUser' | 'inviteStaff';

/** State of the secondary panel */
export interface SecondaryPanelState {
  type: SecondaryPanelType;
  /** Quick action ID when type is 'quickAction' */
  quickActionId: QuickActionId | null;
  /** Section context */
  sectionId: string | null;
}

const INITIAL_STATE: SecondaryPanelState = {
  type: null,
  quickActionId: null,
  sectionId: null,
};

/**
 * Service to manage the state of Secondary Panel.
 * Handles quick action forms (create user, invite staff).
 */
@Injectable({ providedIn: 'root' })
export class SecondaryPanelService {
  private readonly _state = signal<SecondaryPanelState>(INITIAL_STATE);

  /** Current panel state */
  readonly state = this._state.asReadonly();

  /** True if any panel content is open */
  readonly isOpen = computed(() => this._state().type !== null);

  /** True if quick action panel is open */
  readonly isQuickAction = computed(() => this._state().type === 'quickAction');

  /** Current quick action ID (when quick action is open) */
  readonly quickActionId = computed(() => this._state().quickActionId);

  /** Current section ID context */
  readonly sectionId = computed(() => this._state().sectionId);

  /**
   * Open quick action panel
   * @param actionId The quick action to open
   * @param sectionId The section context
   */
  openQuickAction(actionId: QuickActionId, sectionId: string): void {
    this._state.set({
      type: 'quickAction',
      quickActionId: actionId,
      sectionId,
    });
  }

  /**
   * Toggle quick action. If already open with the same action, close it.
   * Otherwise, open the specified action.
   */
  toggleQuickAction(actionId: QuickActionId, sectionId: string): void {
    const current = this._state();
    if (current.type === 'quickAction' && current.quickActionId === actionId) {
      this.close();
    } else {
      this.openQuickAction(actionId, sectionId);
    }
  }

  /** Close the secondary panel */
  close(): void {
    this._state.set(INITIAL_STATE);
  }
}
